/**
 * dockEmbed.js — a tool page running INSIDE the /acp ToolsDock.
 *
 * The dock shows every tool in its own iframe of `/tools/<id>?embed=1`, so the
 * tool's viewport IS the dock column and its existing responsive design renders
 * the phone layout — no tool page changes its own styling for this. Two things
 * differ from the normal page, and both key off isDockEmbedded():
 *
 *   1. chrome — no sidebar (PageLayout), no floating ACP quick view
 *      (AcpQuickView; the panel next to the dock plays that part), no dev
 *      role-preview bar.
 *   2. default user — the account open in the ACP panel is the tool's default
 *      search: useDockAcpUser() prefills the input and runs the lookup, first
 *      from the URL (acpUser / acpName / acpMarket, set when the tab opened) and
 *      then live from the parent's postMessage whenever the panel moves to
 *      another account.
 *
 * On the real /tools pages (not framed) the URL plays the panel's part
 * (2026-10-13): /tools/<id>/<userId>[?market=] opens the tool on that user and
 * searches it, and every search that resolves a user writes that link into the
 * address bar — so it can be copied and sent. The rest of this file is inert there.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

/** parent → tool: { type, detail: { id, name, market } | null } */
export const DOCK_USER_MESSAGE = 'clara:dock-acp-user';
/** tool → parent: a search bar resolved a user (same name as the window event). */
export const DOCK_QUICKVIEW_MESSAGE = 'clara:acp-quickview-open';

export function isFramed() {
  try { return window.self !== window.top; } catch { return true; }
}

/**
 * Running inside the dock = running framed. Nothing else frames Clara (the
 * CSP forbids it), and keying off the frame alone keeps the embed chrome
 * through in-frame navigation (⌘K quick search inside a tab), where the
 * ?embed=1 marker on the initial URL would be lost.
 */
export function isDockEmbedded() {
  return isFramed();
}

/** The account the dock opened this tab with (query string), or null. */
export function readDockUserFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    const id = p.get('acpUser');
    if (!id) return null;
    return { id, name: p.get('acpName') || null, market: p.get('acpMarket') || 'com' };
  } catch {
    return null;
  }
}

/** Build the dock iframe URL for a tool, carrying the current ACP account. */
export function dockToolUrl(toolId, acpUser) {
  const p = new URLSearchParams({ embed: '1' });
  if (acpUser?.id) {
    p.set('acpUser', acpUser.id);
    if (acpUser.name) p.set('acpName', acpUser.name);
    if (acpUser.market) p.set('acpMarket', acpUser.market);
  }
  return `/tools/${toolId}?${p.toString()}`;
}

/** Tool → dock parent. False when not framed. */
export function postToParent(type, detail) {
  if (!isFramed()) return false;
  try {
    window.parent.postMessage({ type, detail }, window.location.origin);
    return true;
  } catch {
    return false;
  }
}

/**
 * Wire a tool's search bar to the account it opens on: the dock's current ACP
 * account when framed, the page's own link (/tools/<id>/<userId>) when not.
 *
 *   useDockAcpUser({ setTerm: setUserId, run: runCheck, onUser });
 *
 * - `setTerm(username || id)` prefills the input as soon as an account arrives
 *   (dock: URL at mount, postMessage afterwards; page: the link at mount, its
 *   ?market= — default com — as the account's market; the same id is applied once).
 * - `run` (optional) is the tool's own submit handler; it is called on the
 *   render that carries the prefill AND onUser's market switch, so its closure
 *   already sees both — the tool needs no refactor. A stub event is passed for
 *   handlers that call e.preventDefault().
 * - `onUser(u)` (optional) fires before the prefill — e.g. to switch the tool's
 *   market to the account's market.
 * - page only: a user the tool's search resolved (announceQuickView fires the
 *   window event named like DOCK_QUICKVIEW_MESSAGE) replaces the URL with
 *   /tools/<id>/<userId>, plus ?market= off .com. Same route — no remount, no fetch.
 *
 * Returns whether the tool is embedded in the dock.
 */
export function useDockAcpUser({ setTerm, run, onUser } = {}) {
  const embedded = isDockEmbedded();
  const { userId: linkedId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const runRef = useRef(run);
  const onUserRef = useRef(onUser);
  const setTermRef = useRef(setTerm);
  const routerRef = useRef(null);
  runRef.current = run;
  onUserRef.current = onUser;
  setTermRef.current = setTerm;
  routerRef.current = { navigate, pathname: location.pathname };
  const lastIdRef = useRef(null);
  const [runs, setRuns] = useState(0);

  useEffect(() => {
    const apply = (u) => {
      if (!u || typeof u.id !== 'string' || !u.id) return;
      if (u.id === lastIdRef.current) return;
      lastIdRef.current = u.id;
      try { onUserRef.current?.(u); } catch { /* a tool's market switch must never break the prefill */ }
      setTermRef.current?.(u.name || u.id);
      if (runRef.current) setRuns((n) => n + 1); // batched with both updates above
    };
    if (!embedded) {
      if (linkedId) apply({ id: linkedId, name: null, market: new URLSearchParams(location.search).get('market') || 'com' });
      const onResolved = (e) => {
        const { id, market } = e.detail || {};
        if (!id) return;
        const { navigate: go, pathname } = routerRef.current;
        const base = pathname.split('/').slice(0, 3).join('/'); // /tools/<id>
        go(`${base}/${encodeURIComponent(id)}${market && market !== 'com' ? `?market=${market}` : ''}`, { replace: true });
      };
      window.addEventListener(DOCK_QUICKVIEW_MESSAGE, onResolved);
      return () => window.removeEventListener(DOCK_QUICKVIEW_MESSAGE, onResolved);
    }
    apply(readDockUserFromUrl());
    const onMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      if (!e.data || e.data.type !== DOCK_USER_MESSAGE) return;
      apply(e.data.detail || null);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // Mount-only: the link is read once; later URL changes are this hook's own writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded]);

  // The prefill landed in state → run the tool's own handler, once per account.
  useEffect(() => {
    if (!runs) return;
    try { runRef.current?.({ preventDefault() {} }); } catch { /* the tool reports its own errors */ }
  }, [runs]);

  return embedded;
}
